import { EntityDataModel } from "./EntityDataModel.mjs";
import { EntityPersistence } from "./EntityPersistence.mjs";
//Leverages the FETCH api (node or browser) to persist Entities via REST
class FrankServiceEntityPersistence extends EntityPersistence {
    #_baseUri;
    #_entityName;
    #_customHeaderFn;
    constructor(baseUri, entityName, customHeaderFn = async function () { return {} }) {
        super();
        this.#_baseUri = baseUri;
        this.#_entityName = entityName;
        this.#_customHeaderFn = customHeaderFn;
    }
    #_validateId(id) {
        if (!id || (id && typeof (id) !== 'string')) {
            throw new Error('INVALID_ID');
        }
    }
    #_getEntityBaseUri() {
        return `${this.#_baseUri}/${this.#_entityName}`;
    }
    #_getEntityCollectionBaseUri() {
        if (
            this.#_entityName.toLowerCase().endsWith('ch') ||
            this.#_entityName.toLowerCase().endsWith('sh') ||
            this.#_entityName.toLowerCase().endsWith('x') ||
            this.#_entityName.toLowerCase().endsWith('s')
        ) {
            return `${this.#_baseUri}/${this.#_entityName}es`;
        }
        else {
            return `${this.#_baseUri}/${this.#_entityName}s`;
        }
    }
    //The header function is told which request it is for: a caller's credential may be a proof made for that one request
    async #_getCustomHeaders(method, url) {
        return await this.#_customHeaderFn({ method, url });
    }
    /*****************************************************/
    // START CREATION METHODS
    /*****************************************************/
    async create(entityDataModel) {
        if (entityDataModel.id && typeof (entityDataModel.id) !== 'string') {
            throw new Error('INVALID_ID');
        }
        const url = `${this.#_getEntityCollectionBaseUri()}`;
        const customHeaders = await this.#_getCustomHeaders("POST", url);
        const response = await fetch(url, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(entityDataModel.toJSON(true)),
        });
        switch (response.status) {
            case 409:
                throw new Error('DUPLICATE_UNIQUE_KEY');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.fromJSON(responsePayload.inserted);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async clone(entityDataModel, idOfClone) {
        const idOfEntityBeingCloned = entityDataModel.id;
        this.#_validateId(idOfEntityBeingCloned);
        const url = `${this.#_getEntityBaseUri()}/${idOfEntityBeingCloned}/clone`;
        const customHeaders = await this.#_getCustomHeaders("POST", url);
        const requestBody = { clonedId: idOfClone };
        const response = await fetch(url, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 409:
                throw new Error('DUPLICATE_UNIQUE_KEY');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.cloned);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    /*****************************************************/
    // END CREATION METHODS
    // START UPSERT METHODS
    /*****************************************************/
    async upsert(entityDataModel) {
        const document = entityDataModel.toJSON(true);
        const url = `${this.#_getEntityCollectionBaseUri()}/upsert`;
        const customHeaders = await this.#_getCustomHeaders("PUT", url);
        const requestBody = document;
        const response = await fetch(url, {
            method: "PUT",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.upserted);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    /*****************************************************/
    // END UPSERT METHODS
    // START UPDATE METHODS
    /*****************************************************/
    async update(entityDataModel) {
        const entityId = entityDataModel.id;
        this.#_validateId(entityId);
        const partialDocument = entityDataModel.toJSON(true);
        delete partialDocument.id;//should not allow them to set the id in the partialDocument
        const url = `${this.#_getEntityBaseUri()}/${entityId}`;
        const customHeaders = await this.#_getCustomHeaders("PATCH", url);
        const requestBody = partialDocument;
        const response = await fetch(url, {
            method: "PATCH",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.updated);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async patch(entityDataModel, patch) {
        const entityId = entityDataModel.id;
        this.#_validateId(entityId);
        const url = `${this.#_getEntityBaseUri()}/${entityId}`;
        const customHeaders = await this.#_getCustomHeaders("PATCH", url);
        const requestBody = patch;
        const response = await fetch(url, {
            method: "PATCH",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json-patch+json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.patched);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async append(entityDataModel, pathToArray, arrayElement) {
        const entityId = entityDataModel.id;
        this.#_validateId(entityId);
        const url = `${this.#_getEntityBaseUri()}/${entityId}/append`;
        const customHeaders = await this.#_getCustomHeaders("PUT", url);
        const requestBody = {
            field: pathToArray,
            value: arrayElement
        };
        const response = await fetch(url, {
            method: "PUT",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.appended);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    /*****************************************************/
    // END UPDATE METHODS
    // START REPLACE METHODS
    /*****************************************************/
    async replace(entityDataModel, idToReplace = entityDataModel?.id) {
        try {
            this.#_validateId(idToReplace);
        } catch (e) {
            if (e.message === 'INVALID_ID') {
                e.message = 'ID_TO_REPLACE_IS_INVALID';
            }
            throw e;
        }
        const document = entityDataModel.toJSON(true);
        delete document.id;//should not allow them to set the id in the partialDocument
        const url = `${this.#_getEntityBaseUri()}/${idToReplace}/replace`;
        const customHeaders = await this.#_getCustomHeaders("PUT", url);
        const requestBody = document;
        const response = await fetch(url, {
            method: "PUT",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body: JSON.stringify(requestBody)
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.replaced);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    /*****************************************************/
    // END REPLACE METHODS
    // START RETRIEVAL METHODS
    /*****************************************************/
    #_getProjectionQueryParams(projection) {
        let url = '';
        if (projection) {
            url += `projection-type=${projection.type || 'include'}`;
            if (projection.type === 'exclude') {
                url += `&projection-exclude-properties=${projection.excludeProperties}`;
            }
            else {
                url += `&projection-include-properties=${projection.includeProperties}`;
            }
        }
        return url;
    }
    //`GET /<SINGULAR_ENTITY_NAME>/<ID>?projection`
    async retrieve(entityDataModel, projection = undefined) {
        const entityId = entityDataModel.id;
        this.#_validateId(entityId);
        let url = `${this.#_getEntityBaseUri()}/${entityId}`;
        if (projection) {
            url += `?${this.#_getProjectionQueryParams(projection)}`;
        }
        const customHeaders = await this.#_getCustomHeaders("GET", url);
        const response = await fetch(url, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                entityDataModel.replaceFromJSON(responsePayload.retrieved);
                return entityDataModel;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async retrieveByIds(ids, projection = undefined) {
        let url = `${this.#_getEntityCollectionBaseUri()}`;
        const customHeaders = await this.#_getCustomHeaders("GET", url);
        url += `?filter=by-ids&ids=${JSON.stringify(ids)}`;
        if (projection) {
            url += `&${this.#_getProjectionQueryParams(projection)}`;
        }
        const response = await fetch(url, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
        });
        switch (response.status) {
            case 200:
                const responsePayload = await response.json();
                const entityDataModelArray = [];
                for (const retrievedDoc of responsePayload.retrieved) {
                    const retrievedEntityDataModel = new EntityDataModel(retrievedDoc);
                    entityDataModelArray.push(retrievedEntityDataModel);
                }
                return {
                    retrievedCount: entityDataModelArray.length,
                    retrievedEntities: entityDataModelArray
                };
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async retrieveByProperties(propertiesFilter, projection = undefined) {
        let url = `${this.#_getEntityCollectionBaseUri()}`;
        const customHeaders = await this.#_getCustomHeaders("GET", url);
        let search = '';
        for (const [propName, propValue] of Object.entries(propertiesFilter)) {
            if (search !== '') {
                search += `&`;
            }
            search += `${propName}=${propValue}`;
        }
        if (search !== '') {
            url += `?${search}`;
        }
        if (projection) {
            const projectionQueryParams = this.#_getProjectionQueryParams(projection);
            if (search === '') {
                url += `?${projectionQueryParams}`;
            }
            else {
                url += `&${projectionQueryParams}`;
            }
        }
        const response = await fetch(url, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
        });
        switch (response.status) {
            case 200:
                const responsePayload = await response.json();
                const entityDataModelArray = [];
                for (const retrievedDoc of responsePayload.retrieved) {
                    const retrievedEntityDataModel = new EntityDataModel(retrievedDoc);
                    entityDataModelArray.push(retrievedEntityDataModel);
                }
                return {
                    retrievedCount: entityDataModelArray.length,
                    retrievedEntities: entityDataModelArray
                };
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    async retrieveAll(projection = undefined) {
        return await this.retrieveByProperties({}, projection);
    }
    /*****************************************************/
    // END RETRIEVAL METHODS
    // START DESTRUCTIVE METHODS
    /*****************************************************/
    //`DELETE /<SINGULAR_ENTITY_NAME>/<ID>`
    async delete(entityDataModel) {
        const entityId = entityDataModel.id;
        this.#_validateId(entityId);
        const url = `${this.#_getEntityBaseUri()}/${entityId}`
        const customHeaders = await this.#_getCustomHeaders("DELETE", url);
        const response = await fetch(url, {
            method: "DELETE",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
        });

        switch (response.status) {
            case 404:
                throw new Error('ID_NOT_FOUND');
            // break;
            case 200:
                const responsePayload = await response.json();
                return responsePayload;
            // break;
            default:
                const err = new Error(`Encountered status code: ${response.status} ${response.statusText}.`)
                throw err;
            // break;
        }
    }
    /*****************************************************/
    // END DESTRUCTIVE METHODS
    /*****************************************************/
}
export { FrankServiceEntityPersistence };
export default FrankServiceEntityPersistence;